import React, { useEffect, useState } from "react";
import { Box, Snackbar, Alert, GlobalStyles, useTheme, useMediaQuery } from '@mui/material';
import axiosInstance, {publicInstance} from '../axiosInstance';
import CurrentServiceComments from './CurrentServiceComments';
import ServiceHistory from './ServiceHistory';
import { useOutletContext } from 'react-router-dom';
import { useRefresh } from '../../Components/RefreshContext';
import { useAtom } from 'jotai';
import { serviceCommentsCountAtom } from '../../helpers/atoms';

const RepairPanel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentComments, setCurrentComments] = useState([]);
  const [historyComments, setHistoryComments] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [users, setUsers] = useState([]);
  const { handleRepair, updateServiceCommentsCount } = useOutletContext<{ handleRepair: (box: number) => void, updateServiceCommentsCount: (count: number) => void }>();
  const { refresh } = useRefresh();
  const [, setServiceCommentsCount] = useAtom(serviceCommentsCountAtom);
  const [isLoading, setIsLoading] = useState(true); 

  useEffect(() => {
    const fetchCurrentComments = () => {
      setIsLoading(true); 
      publicInstance.get('/view_dacapi_sensorbox_service_comments')
        .then(response => {
          const sortedData = response.data.sort((a, b) => 
            new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime()
          );
          setCurrentComments(sortedData);
          updateServiceCommentsCount(sortedData.length);
          setServiceCommentsCount(sortedData.length); 
        })
        .catch(error => console.error('Error fetching service comments:', error))
        .finally(() => setIsLoading(false)); 
    };

    const fetchHistoryComments = () => {
      publicInstance.get('/dacapi_sensorbox_service_comments')
        .then(response => {
          const sortedData = response.data.sort((a, b) => new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime());
          setHistoryComments(sortedData);
        })
        .catch(error => console.error('Error fetching history comments:', error));
    };

    const fetchUsers = () => {
      axiosInstance.get('/auth/users')
        .then(response => {
          setUsers(response.data);
        })
        .catch(error => console.error('Error fetching users:', error));
    };

    fetchCurrentComments();
    fetchHistoryComments();
    fetchUsers();
    }, [refresh, updateServiceCommentsCount, setServiceCommentsCount]);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      // Refresh current comments
      const currentResponse = await publicInstance.get('/view_dacapi_sensorbox_service_comments');
      const sortedCurrentData = currentResponse.data.sort((a, b) => 
        new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime()
      );
      setCurrentComments(sortedCurrentData);
      updateServiceCommentsCount(sortedCurrentData.length);
      setServiceCommentsCount(sortedCurrentData.length);

      // Refresh history comments
      const historyResponse = await publicInstance.get('/dacapi_sensorbox_service_comments');
      const sortedHistoryData = historyResponse.data.sort((a, b) => 
        new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime()
      );
      setHistoryComments(sortedHistoryData);
      
      refresh();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarClose = (event: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', py: 4 }}>
      
      <Box sx={{ width: '85%', margin: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', mb: 4 }}>
          <CurrentServiceComments comments={currentComments} users={users} onDataRefresh={refreshData} />
          <ServiceHistory comments={historyComments} users={users} />
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default RepairPanel;
